import { message } from 'antd';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import useCurrency from '@/hooks/useCurrency';
import { useErrorMsg } from '@/hooks/useErrorMsg';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useI18nContext } from '@/i18n/I18n';
import {
  getApplyCount,
  getSubmemberList,
  getSubMembersBalance,
} from '@/services/subAccount';
import {
  IApplyCountResult,
  IContractAsset,
  IListParams,
  ISubAccountRecord,
  ItemBalance,
} from '@/services/types/subAccount';
import { REGULATED_SITE_HOSTS } from '@/utils/constants/compliance';
import { SubMemberType } from '@/utils/constants/sub-account';

export function useSubAccount() {
  const [$t] = useI18nContext();
  const { mutate, currencyCode, profile, userInfo } = useUserProfile();
  const { msgError } = useErrorMsg(mutate);
  const [currentCurrencyCode, setCurrentCurrencyCode] = useState('USD');
  const [balances, setBalances] = useState<IContractAsset>({ balances: {} });

  const { showCurrencyOfflineModal, checkUserCurrencyCode } = useCurrency();

  // 子账号总资产state
  const [hasSubAccount, setHasSubAccount] = useState(false);
  const [eachChildContractBalance, setEachChildContractBalance] = useState<any>(
    {},
  );
  const [search] = useSearchParams();
  // Table state
  const [tableLoading, setTableLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const pageSizeOptions = [10, 20, 50];
  const [pagination, setPagination] = useState({
    total: 0,
    current: +(search.get('page') || 1),
    pageSize: 10,
    pageSizeOptions,
  });
  // 交易团队state
  const [isExchangeTeam, setIsExchangeTeam] = useState(false);
  const [bindRequestCount, setBindRequestCount] = useState(0);

  // 处理子账号资产(现货、合约、usdc合约、统保)数据
  const handleBalances = (balances: IContractAsset) => {
    const eachContractBalance: any = {};

    const isNLDSite = REGULATED_SITE_HOSTS === 'NLD';

    const findCb = (type: string) => (item: ItemBalance) =>
      item.accountType === type;

    // "ACCOUNT_TYPE_SPOT" // 现货
    // "ACCOUNT_TYPE_CONTRACT" // 合约
    // "ACCOUNT_TYPE_INVESTMENT" // 理财
    // "ACCOUNT_TYPE_OPTION" // 期权
    // "ACCOUNT_TYPE_UNIFIED" // 统保 - 会判断是否开通账户（业务场景如此，不够条件的不能够升级统保）
    // "ACCOUNT_TYPE_FUND" // 资金账户
    // "ACCOUNT_TYPE_BOT" // bot - 会判断是否开通账户（业务场景如此）
    // "ACCOUNT_TYPE_MT4" // MT4 - 会判断是否开通账户（业务场景如此）
    // "ACCOUNT_TYPE_COPY_TRADE" // copytrade - 会判断是否开通账户（业务场景如此）

    const balancesCombination: {
      [key: string]: Array<{ label: string; value: string }>;
    } = {
      common: [
        {
          value: 'ACCOUNT_TYPE_SPOT',
          label: 'spotAccount',
        },
        ...(isNLDSite
          ? []
          : [
              {
                value: 'ACCOUNT_TYPE_CONTRACT',
                label: 'Contract Account',
              },
              // {
              //   value: 'ACCOUNT_TYPE_OPTION',
              //   label: 'USDCDerivativesAccount',
              // },
            ]),
        {
          value: 'ACCOUNT_TYPE_FUND',
          label: 'autoTransferToFund',
        },
        {
          value: 'ACCOUNT_TYPE_INVESTMENT',
          label: 'ByfiAccount',
        },
      ],
      uta: [
        {
          value: 'ACCOUNT_TYPE_SPOT',
          label: 'spotAccount',
        },
        {
          value: 'ACCOUNT_TYPE_UNIFIED',
          label: 'autoTransferToUta',
        },
        ...(isNLDSite
          ? []
          : [
              {
                value: 'ACCOUNT_TYPE_CONTRACT',
                label: 'inverseDerivativesAccount',
              },
            ]),
        {
          value: 'ACCOUNT_TYPE_FUND',
          label: 'autoTransferToFund',
        },
        {
          value: 'ACCOUNT_TYPE_INVESTMENT',
          label: 'ByfiAccount',
        },
      ],
      unified: [
        {
          value: 'ACCOUNT_TYPE_SPOT',
          label: 'spotAccount',
        },
        { value: 'ACCOUNT_TYPE_UNIFIED', label: 'unifiedAccount' },
        ...(isNLDSite
          ? []
          : [
              {
                value: 'ACCOUNT_TYPE_CONTRACT',
                label: 'inverseDerivativesAccount',
              },
            ]),
        {
          value: 'ACCOUNT_TYPE_FUND',
          label: 'autoTransferToFund',
        },
        {
          value: 'ACCOUNT_TYPE_INVESTMENT',
          label: 'ByfiAccount',
        },
      ],
    };

    Object.entries(balances.balances).forEach(
      ([subAccountId, subAccountContractInfo]) => {
        eachContractBalance[subAccountId] = {
          totalBalanceBtc: subAccountContractInfo?.totalBalanceBtc,
          totalBalanceUsdt: subAccountContractInfo?.totalBalanceUsdt,
        };
        const userType = subAccountContractInfo.userType || 'common';
        balancesCombination[userType]?.forEach((item) => {
          const balance = subAccountContractInfo.accountBalance.find(
            findCb(item.value),
          )?.accountBalanceBtc;
          if (balance) {
            eachContractBalance[subAccountId][item.label] = balance;
          }
        });
      },
    );
    setEachChildContractBalance(eachContractBalance);
  };
  // 获取子账号资产数据
  const getContract = (subMemberIds: string) => {
    getSubMembersBalance(subMemberIds)
      .then((res) => {
        const balances = (res?.result as IContractAsset) || {};
        setBalances(balances);
      })
      .catch((error) => {
        message.error(`${error?.ret_code}: ${error?.ret_msg}`);
      });
  };
  // 获取子账号列表数据
  const getDataSource = (params: IListParams) => {
    setTableLoading(true);
    getSubmemberList(params)
      .then((res: any) => {
        if (res.result && res.result.members) {
          setHasSubAccount(true);
          setDataSource(
            res.result.members.map((item: any) => ({
              ...item,
              loginName:
                item.loginName ||
                (item.memberType === SubMemberType.COPY_TRADING_ACCOUNT
                  ? $t('child.masterTrader')
                  : item.loginName),
              copyTradingUpgradeNoLoginName:
                item.memberType === SubMemberType.COPY_TRADING_ACCOUNT &&
                item.copytradeUpgrade === 'SUCCESS' &&
                !item.loginName,
              isCopypro: item.memberType === SubMemberType.COPY_PRO_ACCOUNT,
              // 前端自定义的字段，用于根据类型进行UI展示
              // 该字段的值有可能跟后端返回的 memberType 相同，也可能不同
              // 现在只有 伊斯兰子账户 是自定义类型
              frontMemberType: item.isIslamic
                ? SubMemberType.ISLAMIC_ACCOUNT
                : item.memberType,
            })),
          );
          const currentPage = res.result.page;
          if (currentPage !== params.page && search.get('page')) {
            const url = new URL(window.location.href);
            url.searchParams.set('page', currentPage);

            window.history.replaceState(null, '', url.toString());
          }
          setPagination({
            total: res.result.total,
            current: currentPage,
            pageSize: params.pageSize,
            pageSizeOptions,
          });
          if (res.result.members.length) {
            const subMemberIds: string[] = [];
            res.result.members.forEach((member: ISubAccountRecord) => {
              subMemberIds.push(member.memberId);
            });
            // 获取子账号所有资产
            getContract(subMemberIds.join(','));
          }
        }
      })
      .catch((error) => {
        msgError(error);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  // 获取子账户Usdt 若<0.001usdt 则允许删除该子账户
  const getSubMemberUsdt = (memberId: string) => {
    let totalUsdt = 0;
    if (eachChildContractBalance[memberId]) {
      totalUsdt = eachChildContractBalance[memberId]?.totalBalanceUsdt;
    }

    return totalUsdt;
  };

  // 交易团队
  const queryApplyAccount = () => {
    getApplyCount()
      .then((res) => {
        const { exchangeMemberId, count } = res as IApplyCountResult;
        if (exchangeMemberId !== '0') {
          setIsExchangeTeam(true);
          setBindRequestCount(Number(count) || 0);
        }
      })
      .catch((error) => {
        msgError(error);
      });
  };

  useEffect(() => {
    // 获取子账号列表
    getDataSource({ page: pagination.current || 1, pageSize: 10, switch: 0 });
    // 获取是否是交易团队
    queryApplyAccount();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pagination.pageSize]);

  useEffect(() => {
    if (currencyCode) {
      checkUserCurrencyCode(currencyCode)?.then((res) => {
        if (res?.is_offline === 1) {
          setCurrentCurrencyCode('USD');
        } else {
          setCurrentCurrencyCode(currencyCode);
        }
      });
    }
  }, [currencyCode]);

  useEffect(() => {
    handleBalances(balances);
  }, [balances]);

  return {
    profile,
    currencyCode: currentCurrencyCode,
    pagination,
    tableLoading,
    dataSource,
    eachChildContractBalance,
    hasSubAccount,
    userInfo,
    getDataSource,
    getSubMemberUsdt,
    msgError,
    showCurrencyOfflineModal,
    isExchangeTeam,
    bindRequestCount,
  };
}
